import type { IField } from "screens/common/components";
import type { useTickerTypeOptions } from "./tickerTypesOptions";
import type { useExchangeOptions } from "./exchangeOptions";
import type { usePricingTierOptions } from "./pricingTierOptions";

export interface TickerFormFieldsProps {
  exchangeOptions: ReturnType<typeof useExchangeOptions>;
  tickerTypeOptions: ReturnType<typeof useTickerTypeOptions>;
  pricingTiers: ReturnType<typeof usePricingTierOptions>;
  statusOptions: Array<{ value: string; label: string }>;
}

export const getTickerFormFields = ({
  exchangeOptions,
  tickerTypeOptions,
  pricingTiers,
  statusOptions,
}: TickerFormFieldsProps): IField<any>[] => [
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "select",
        entity: "status",
        label: "Status",
        isRequired: true,
        options: statusOptions,
        defaultValue: "active",
      },
      {
        type: "text",
        entity: "symbol",
        label: "Symbol",
        isRequired: true,
        defaultValue: "",
      },
      {
        type: "select",
        entity: "exchange",
        label: "Exchange",
        isRequired: true,
        options: exchangeOptions,
        defaultValue: "",
      },
    ],
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "text",
        entity: "name",
        label: "Name",
        isRequired: true,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "companyLegalName",
        label: "Company Legal Name",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "aliases",
        label: "Aliases (comma-separated)",
        isRequired: false,
        defaultValue: "",
      },
    ],
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "text",
        entity: "companyLogoUrl",
        label: "Company Logo URL",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "companyBannerUrl",
        label: "Company Banner URL",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "companyUrl",
        label: "Company Website URL",
        isRequired: false,
        defaultValue: "",
      },
    ],
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "text",
        entity: "companyTaxId",
        label: "Company Tax ID",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "select",
        entity: "type",
        label: "Type",
        isRequired: true,
        options: [{ label: "Not added", value: "null" }, ...tickerTypeOptions],
        defaultValue: "",
      },
      {
        type: "select",
        entity: "pricingTier",
        options: [{ label: "Not added", value: "null" }, ...pricingTiers],
        label: "Pricing Tier",
        isRequired: false,
        defaultValue: "",
      },
    ],
  },
  {
    type: "text",
    entity: "dataProviderId",
    label: "Data Provider Id",
    isRequired: false,
    defaultValue: "",
  },
  {
    type: "textarea",
    entity: "notes",
    label: "Notes",
    isRequired: false,
    minHeight: "5rem",
    defaultValue: "",
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "number",
        entity: "investmentScoreAnalytical",
        label: "Investment Score (Analytical)",
        isRequired: false,
        defaultValue: 0,
        showStepper: false,
      },
      {
        type: "number",
        entity: "investmentScoreSentiment",
        label: "Investment Score (Sentiment)",
        isRequired: false,
        defaultValue: 0,
        showStepper: false,
      },
      {
        type: "number",
        entity: "overallInvestmentScore",
        label: "Overall Investment Score",
        isRequired: false,
        defaultValue: 0,
        showStepper: false,
      },
    ],
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "number",
        entity: "marketCap",
        label: "Market Cap",
        isRequired: false,
        defaultValue: 0,
        showStepper: false,
      },
      {
        type: "number",
        entity: "fairValue",
        label: "Fair Value",
        isRequired: false,
        defaultValue: 0,
        showStepper: false,
      },
      {
        type: "number",
        entity: "revenue",
        label: "Revenue",
        isRequired: false,
        defaultValue: 0,
        showStepper: false,
      },
    ],
  },
  {
    type: "text",
    entity: "address",
    label: "Address",
    isRequired: false,
    defaultValue: "",
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "text",
        entity: "country",
        label: "Country",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "stateOrProvince",
        label: "State/Province",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "city",
        label: "City",
        isRequired: false,
        defaultValue: "",
      },
    ],
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "number",
        entity: "yearFounded",
        label: "Year Founded",
        isRequired: false,
        defaultValue: 0,
        showStepper: false,
      },
      {
        type: "text",
        entity: "companyType",
        label: "Company Type",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "companyLegalStructure",
        label: "Legal Structure",
        isRequired: false,
        defaultValue: "",
      },
    ],
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "text",
        entity: "ipoStatus",
        label: "IPO Status",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "companyTradingStatus",
        label: "Trading Status",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "companyStockExchangeStatus",
        label: "Stock Exchange Status",
        isRequired: false,
        defaultValue: "",
      },
    ],
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "text",
        entity: "companyCreditScore",
        label: "Credit Score",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "raisingCapital",
        label: "Raising Capital",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "raisingCapitalAmount",
        label: "Raising Capital Amount",
        isRequired: false,
        defaultValue: "",
      },
    ],
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "text",
        entity: "companyLinkedinUrl",
        label: "LinkedIn URL",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "companyCrunchbaseUrl",
        label: "Crunchbase URL",
        isRequired: false,
        defaultValue: "",
      },
    ],
  },
  {
    type: "textarea",
    entity: "companyDescription",
    label: "Company Description",
    isRequired: false,
    minHeight: "5rem",
    defaultValue: "",
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "text",
        entity: "dataProviderConfidenceScore",
        label: "Data Provider Confidence Score",
        isRequired: false,
        defaultValue: "",
      },
      {
        type: "text",
        entity: "inferredCompanyStage",
        label: "Inferred Company Stage",
        isRequired: false,
        defaultValue: "",
      },
    ],
  },
  {
    type: "stack",
    isInline: true,
    spacing: "2rem",
    fields: [
      {
        type: "number",
        entity: "inferredGrowthRate",
        label: "Inferred Growth Rate",
        isRequired: false,
        defaultValue: 0,
        showStepper: false,
      },
      {
        type: "number",
        entity: "inferredRevenue",
        label: "Inferred Revenue",
        isRequired: false,
        defaultValue: 0,
        showStepper: false,
      },
      {
        type: "number",
        entity: "numberOfEmployees",
        label: "Number of Employees",
        isRequired: false,
        defaultValue: 0,
        showStepper: false,
      },
    ],
  },
];
